// components/appointments/AppointmentCallData.jsx
import React, { useState, useEffect } from "react";
import { Phone, Clock, User, TrendingUp, FileText, Play } from "lucide-react";
import apiService from "../../services/api";
import { useTheme } from "../../contexts/ThemeContext";

const AppointmentCallData = ({ appointmentId }) => {
  const { isDarkMode } = useTheme();
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCall, setSelectedCall] = useState(null);

  useEffect(() => {
    fetchCallData();
  }, [appointmentId]);

  const fetchCallData = async () => {
    try {
      setLoading(true);
      const response = await apiService.getAppointmentCalls(appointmentId);
      if (response.success) {
        setCalls(response.calls);
        if (response.calls.length > 0) {
          setSelectedCall(response.calls[0]);
        }
      }
    } catch (error) {
      console.error("Error fetching call data:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div
        className={`${isDarkMode ? "bg-[#13161B] border-[#22262F]" : "bg-white border-gray-200"} border rounded-xl p-6`}
      >
        <div
          className={`text-center ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
        >
          Loading call data...
        </div>
      </div>
    );
  }

  if (calls.length === 0) {
    return (
      <div
        className={`${isDarkMode ? "bg-[#13161B] border-[#22262F]" : "bg-white border-gray-200"} border rounded-xl p-6`}
      >
        <div
          className={`text-center ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
        >
          <Phone size={48} className="mx-auto mb-3 opacity-50" />
          <p>No call recordings found for this appointment</p>
        </div>
      </div>
    );
  }

  const call = selectedCall?.callData;

  return (
    <div className="space-y-4">
      {/* Call List */}
      {calls.length > 1 && (
        <div
          className={`${isDarkMode ? "bg-[#13161B] border-[#22262F]" : "bg-white border-gray-200"} border rounded-xl p-4`}
        >
          <h3
            className={`text-sm font-medium ${isDarkMode ? "text-gray-400" : "text-gray-500"} mb-3`}
          >
            Related Calls
          </h3>
          <div className="space-y-2">
            {calls.map((c) => (
              <button
                key={c.id}
                onClick={() => setSelectedCall(c)}
                className={`w-full text-left p-3 rounded-lg transition-colors ${
                  selectedCall?.id === c.id
                    ? "bg-blue-500/10 border border-blue-500/30"
                    : isDarkMode
                      ? "bg-[#0C0E12] border border-gray-700 hover:border-gray-600"
                      : "bg-gray-50 border border-gray-200 hover:border-gray-300"
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Phone
                      size={16}
                      className={isDarkMode ? "text-gray-400" : "text-gray-500"}
                    />
                    <span
                      className={`text-sm ${isDarkMode ? "text-white" : "text-gray-900"}`}
                    >
                      {c.phoneNumber}
                    </span>
                  </div>
                  <span
                    className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  >
                    {new Date(c.callStartTime).toLocaleString()}
                  </span>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Call Details */}
      <div
        className={`${isDarkMode ? "bg-[#13161B] border-[#22262F]" : "bg-white border-gray-200"} border rounded-xl p-6`}
      >
        <div className="flex items-center justify-between mb-6">
          <h3
            className={`text-lg font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}
          >
            Call Recording
          </h3>
          {call?.recordingUrl && (
            <a
              href={call.recordingUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
            >
              <Play size={14} />
              Play Recording
            </a>
          )}
        </div>

        {/* Call Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div
            className={`${isDarkMode ? "bg-[#0C0E12] border-gray-700" : "bg-gray-50 border-gray-200"} border rounded-lg p-3`}
          >
            <div className="flex items-center gap-2 mb-2">
              <Clock
                size={16}
                className={isDarkMode ? "text-gray-400" : "text-gray-500"}
              />
              <span
                className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
              >
                Duration
              </span>
            </div>
            <div
              className={`text-lg font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}
            >
              {Math.floor(call?.duration / 60)}m {call?.duration % 60}s
            </div>
          </div>

          <div
            className={`${isDarkMode ? "bg-[#0C0E12] border-gray-700" : "bg-gray-50 border-gray-200"} border rounded-lg p-3`}
          >
            <div className="flex items-center gap-2 mb-2">
              <User
                size={16}
                className={isDarkMode ? "text-gray-400" : "text-gray-500"}
              />
              <span
                className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
              >
                Direction
              </span>
            </div>
            <div
              className={`text-lg font-semibold ${isDarkMode ? "text-white" : "text-gray-900"} capitalize`}
            >
              {call?.direction}
            </div>
          </div>

          {call?.qualification && (
            <>
              <div
                className={`${isDarkMode ? "bg-[#0C0E12] border-gray-700" : "bg-gray-50 border-gray-200"} border rounded-lg p-3`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <TrendingUp
                    size={16}
                    className={isDarkMode ? "text-gray-400" : "text-gray-500"}
                  />
                  <span
                    className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  >
                    Qualification
                  </span>
                </div>
                <div
                  className={`text-lg font-semibold ${
                    call.qualification.isQualified
                      ? "text-green-400"
                      : "text-red-400"
                  }`}
                >
                  {call.qualification.isQualified
                    ? "Qualified"
                    : "Not Qualified"}
                </div>
              </div>

              <div
                className={`${isDarkMode ? "bg-[#0C0E12] border-gray-700" : "bg-gray-50 border-gray-200"} border rounded-lg p-3`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <FileText
                    size={16}
                    className={isDarkMode ? "text-gray-400" : "text-gray-500"}
                  />
                  <span
                    className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  >
                    Score
                  </span>
                </div>
                <div
                  className={`text-lg font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}
                >
                  {call.qualification.qualificationScore}/100
                </div>
              </div>
            </>
          )}
        </div>

        {/* Summary */}
        {call?.insights?.summary && (
          <div className="mb-6">
            <h4
              className={`text-sm font-medium ${isDarkMode ? "text-gray-400" : "text-gray-500"} mb-2`}
            >
              Call Summary
            </h4>
            <p
              className={`text-sm ${isDarkMode ? "text-gray-300" : "text-gray-600"} leading-relaxed`}
            >
              {call.insights.summary}
            </p>
          </div>
        )}

        {/* Transcript Preview */}
        {call?.transcripts && call.transcripts.length > 0 && (
          <div>
            <h4
              className={`text-sm font-medium ${isDarkMode ? "text-gray-400" : "text-gray-500"} mb-3`}
            >
              Transcript
            </h4>
            <div
              className={`${isDarkMode ? "bg-[#0C0E12] border-gray-700" : "bg-gray-50 border-gray-200"} border rounded-lg p-4 max-h-64 overflow-y-auto`}
            >
              <div className="space-y-3">
                {call.transcripts.slice(0, 5).map((transcript, index) => (
                  <div key={index} className="flex gap-3">
                    <div
                      className={`text-xs ${isDarkMode ? "text-gray-500" : "text-gray-400"} min-w-[60px]`}
                    >
                      {Math.floor(transcript.startTime / 60)}:
                      {String(Math.floor(transcript.startTime % 60)).padStart(
                        2,
                        "0",
                      )}
                    </div>
                    <div className="flex-1">
                      <div
                        className={`text-xs font-medium ${isDarkMode ? "text-white" : "text-gray-900"} mb-1`}
                      >
                        {transcript.speakerLabel}:
                      </div>
                      <p
                        className={`text-sm ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}
                      >
                        {transcript.text}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
              {call.transcripts.length > 5 && (
                <div className="text-center mt-3">
                  <button className="text-sm text-blue-400 hover:text-blue-300">
                    View full transcript →
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AppointmentCallData;
